// Inline style objects for the senior gallery island. Colors/shape come from the
// shared `tokens` module (the "Shoob Website v2" Figma palette) so the whole shop
// stays on-brand from one place.
import t from './tokens';

const chip = {
  flex: '0 0 auto',
  whiteSpace: 'nowrap',
  border: `1px solid ${t.borderStrong}`,
  background: t.surface,
  color: t.body,
  borderRadius: t.pill,
  padding: '0.35rem 0.9rem',
  cursor: 'pointer',
  fontSize: '0.85rem',
};

const linkButton = {
  background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontWeight: 600,
};

const styles = {
  wrap: { maxWidth: 1100, margin: '0 auto', padding: '0 1rem' },
  header: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: '0.5rem',
    marginBottom: '1.25rem',
  },
  title: {
    fontSize: '1.6rem', fontWeight: 700, margin: 0, color: t.ink,
  },
  subtitle: { color: t.muted, margin: '0.15rem 0 0', textTransform: 'capitalize' },
  count: { color: t.body, fontSize: '0.9rem', fontWeight: 600 },
  note: { color: t.muted },
  // One cohesive, horizontally-scrollable row of season chips. Scrolling (rather
  // than wrapping) keeps the group from breaking 3+2 across rows at narrow widths.
  seasonTabs: {
    display: 'flex',
    flexWrap: 'nowrap',
    gap: '0.5rem',
    marginBottom: '1.25rem',
    overflowX: 'auto',
    paddingBottom: '0.35rem',
    WebkitOverflowScrolling: 'touch',
    scrollbarWidth: 'thin',
  },
  seasonTab: { ...chip },
  seasonTabActive: {
    ...chip,
    border: `1px solid ${t.brand}`,
    background: t.brand,
    color: '#fff',
    fontWeight: 600,
  },
  seasonMarker: { color: t.star }, // gold star marking the entered season's tab
  // Student switcher (multi-student): a distinct bar above the season tabs.
  studentBar: {
    display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem',
  },
  studentBarLabel: {
    color: t.muted, fontSize: '0.85rem', fontWeight: 600, marginRight: '0.15rem',
  },
  studentTab: {
    ...chip, border: `1px solid ${t.brandBorder}`, color: t.brandHover, background: t.brandTint, fontWeight: 600,
  },
  studentTabActive: {
    ...chip, border: `1px solid ${t.brand}`, background: t.brand, color: '#fff', fontWeight: 700,
  },
  favBar: { marginBottom: '1rem' },
  favToggle: { ...chip, fontWeight: 600 },
  favToggleOn: {
    ...chip,
    border: `1px solid ${t.danger}`,
    background: t.dangerSurface,
    color: t.dangerText,
    fontWeight: 600,
  },
  // Light, dismissible season-context line (replaces the old colored banner).
  codeNote: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    color: t.muted,
    fontSize: '0.85rem',
    margin: '0 0 1rem',
  },
  codeBannerStar: { color: t.star },
  codeNoteX: {
    ...linkButton, color: '#9ca3af', fontSize: '1.1rem', lineHeight: 1, marginLeft: '0.25rem',
  },
  headerActions: {
    display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.5rem',
  },
  packagesBtn: {
    background: t.brandTint,
    color: t.brandHover,
    border: `1px solid ${t.brandBorder}`,
    borderRadius: t.radius,
    padding: '0.4rem 0.8rem',
    fontWeight: 700,
    fontSize: '0.82rem',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  cartTrust: {
    textAlign: 'center', color: t.muted, fontSize: '0.78rem', margin: '0.6rem 0 0',
  },
  empty: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '3rem 1rem', gap: '1rem',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
    gap: '1rem',
  },
  card: {
    margin: 0,
    borderRadius: t.radius,
    overflow: 'hidden',
    background: t.surface,
    boxShadow: t.shadowSm,
    border: '2px solid transparent',
    transition: 'box-shadow 0.15s, border-color 0.15s',
  },
  cardClickable: { cursor: 'pointer' },
  cardSelected: { border: `2px solid ${t.brand}`, boxShadow: t.brandShadow },
  shopHint: { float: 'right', color: t.brand, fontWeight: 600 },
  backdrop: {
    position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.45)', zIndex: 1040,
  },
  panel: {
    position: 'fixed',
    top: 0,
    right: 0,
    bottom: 0,
    width: 'min(420px, 92vw)',
    background: t.surface,
    zIndex: 1050,
    boxShadow: t.shadowPanel,
    padding: '1.25rem',
    overflowY: 'auto',
  },
  panelHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '0.75rem',
  },
  panelTitle: { fontSize: '1.2rem', fontWeight: 700, color: t.ink },
  panelSub: { color: t.muted, fontSize: '0.85rem' },
  close: {
    border: 'none', background: 'none', fontSize: '1.6rem', lineHeight: 1, cursor: 'pointer', color: t.muted,
  },
  panelPreview: {
    width: '100%', height: 200, objectFit: 'cover', borderRadius: t.radius, marginBottom: '1rem',
  },
  // Single element (mirrors the grid tile): the photo fills a fixed 4:5 box via
  // object-fit cover, and the studio background (background-size cover) shows
  // through any transparent areas of a cutout. Because the photo and background
  // share the same box, the background can never exceed the image's frame.
  heroImg: {
    width: '100%',
    aspectRatio: '4 / 5',
    objectFit: 'cover',
    borderRadius: t.radius,
    marginBottom: '1.25rem',
    background: '#f3f4f6',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    display: 'block',
  },
  productGroup: { marginBottom: '1.25rem' },
  productName: {
    fontSize: '0.95rem', fontWeight: 700, margin: '0 0 0.5rem', color: t.ink,
  },
  sectionHint: { fontSize: '0.8rem', color: t.muted, margin: '0 0 0.5rem' },
  quickCard: {
    border: `1px solid ${t.brandBorder}`,
    background: t.brandTint,
    borderRadius: t.radius,
    padding: '0.7rem 0.85rem',
    marginBottom: '0.6rem',
  },
  quickTop: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem',
  },
  quickName: { fontWeight: 700, fontSize: '0.95rem', color: t.ink },
  quickActions: {
    display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '0.5rem',
  },
  quickAdd: {
    flex: '1 1 auto',
    background: t.brand,
    color: '#fff',
    border: 'none',
    borderRadius: t.radius,
    padding: '0.5rem 0.9rem',
    fontWeight: 700,
    cursor: 'pointer',
    fontSize: '0.9rem',
  },
  customizeLink: {
    flex: '0 0 auto', ...linkButton, color: t.brandHover, fontSize: '0.85rem',
  },
  variantRow: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.7rem 0.9rem',
    marginBottom: '0.5rem',
    border: `1px solid ${t.border}`,
    borderRadius: t.radius,
    background: t.surface,
    cursor: 'pointer',
    fontSize: '0.95rem',
  },
  variantPrice: {
    fontWeight: 700, flex: '0 0 auto', marginLeft: 8, color: t.ink,
  },
  variantInfo: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '0.15rem',
    textAlign: 'left',
  },
  badge: {
    marginLeft: 8,
    fontSize: '0.7rem',
    fontWeight: 700,
    color: t.badgeText,
    background: t.badgeSurface,
    borderRadius: t.radiusSm,
    padding: '0.1rem 0.4rem',
  },
  saleEnds: { fontSize: '0.7rem', color: t.dangerText },
  tag: {
    marginLeft: 8,
    fontSize: '0.7rem',
    color: t.brandHover,
    background: t.brandTint,
    borderRadius: t.radiusSm,
    padding: '0.1rem 0.4rem',
  },
  cartBar: {
    position: 'fixed',
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1030,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.85rem 1.25rem',
    background: t.ink,
    color: '#fff',
  },
  backLink: {
    ...linkButton, color: t.brand, marginBottom: '0.35rem', fontSize: '0.85rem',
  },
  configSection: { marginBottom: '1.1rem' },
  configLabel: {
    fontSize: '0.85rem', fontWeight: 700, color: t.body, marginBottom: '0.5rem',
  },
  poseStrip: {
    display: 'flex', gap: '0.5rem', overflowX: 'auto', paddingBottom: '0.25rem',
  },
  poseThumbButton: {
    border: `1px solid ${t.border}`,
    borderRadius: t.radius,
    padding: 0,
    background: t.surface,
    cursor: 'pointer',
    flex: '0 0 auto',
  },
  poseThumb: {
    width: 56,
    height: 70,
    objectFit: 'cover',
    borderRadius: t.radiusSm,
    display: 'block',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundColor: '#f3f4f6',
  },
  bgThumbSelected: {
    border: `2px solid ${t.brand}`,
    borderRadius: t.radius,
    padding: 0,
    background: t.brandTint,
    cursor: 'pointer',
    flex: '0 0 auto',
  },
  bgDefault: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 56,
    height: 70,
    fontSize: '0.7rem',
    color: '#4b5563',
    textAlign: 'center',
    padding: '0 4px',
  },
  slotRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '0.45rem 0',
    borderBottom: `1px solid ${t.hairline}`,
    fontSize: '0.9rem',
  },
  slotName: { flex: '1 1 auto', minWidth: 0 },
  slotControls: { flex: '0 0 auto', display: 'flex', gap: '0.4rem' },
  slotSelect: {
    padding: '0.35rem 0.5rem', borderRadius: t.radiusSm, border: `1px solid ${t.borderStrong}`, maxWidth: 130,
  },
  thumbGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.5rem',
    maxHeight: 240,
    overflowY: 'auto',
    padding: '0.25rem 0',
  },
  thumbSelected: {
    border: `2px solid ${t.brand}`,
    borderRadius: t.radius,
    padding: 0,
    background: t.brandTint,
    cursor: 'pointer',
    flex: '0 0 auto',
  },
  customizeToggle: {
    ...linkButton, color: t.brand, fontSize: '0.9rem',
  },
  printList: { marginTop: '0.75rem' },
  printRow: { borderBottom: `1px solid ${t.hairline}`, padding: '0.5rem 0' },
  printHead: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem',
  },
  miniThumbBtn: {
    border: `1px solid ${t.border}`, borderRadius: t.radius, padding: 0, background: t.surface, cursor: 'pointer',
  },
  miniThumb: {
    width: 40, height: 50, objectFit: 'cover', borderRadius: t.radiusSm, display: 'block',
  },
  miniSwatch: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 40,
    height: 50,
    fontSize: '0.65rem',
    color: t.muted,
  },
  extraNotice: {
    background: '#fffbeb',
    color: t.badgeText,
    border: '1px solid #fde68a',
    borderRadius: t.radius,
    padding: '0.5rem 0.75rem',
    fontSize: '0.85rem',
    marginBottom: '0.9rem',
  },
  configAddButton: {
    width: '100%',
    padding: '0.7rem',
    borderRadius: t.radius,
    border: 'none',
    background: t.brand,
    color: '#fff',
    fontWeight: 700,
    cursor: 'pointer',
  },
  cartItem: { borderBottom: `1px solid ${t.hairline}`, padding: '0.7rem 0' },
  cartItemRow: { display: 'flex', gap: '0.75rem', alignItems: 'flex-start' },
  cartThumb: {
    width: 54, height: 68, objectFit: 'cover', borderRadius: t.radiusSm, flex: '0 0 auto', background: '#e5e7eb',
  },
  cartItemInfo: { flex: '1 1 auto', minWidth: 0 },
  cartItemName: { fontWeight: 600, fontSize: '0.9rem', color: t.ink },
  cartItemActions: { display: 'flex', gap: '0.85rem', marginTop: '0.35rem' },
  linkBtn: {
    ...linkButton, color: t.brand, fontSize: '0.8rem',
  },
  linkBtnDanger: {
    ...linkButton, color: t.dangerText, fontSize: '0.8rem',
  },
  cartPosePicker: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.4rem',
    marginTop: '0.6rem',
    maxHeight: 180,
    overflowY: 'auto',
  },
  cartTotals: {
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    rowGap: '0.3rem',
    columnGap: '1rem',
    margin: '1rem 0',
    fontSize: '0.9rem',
  },
  discountLabel: { color: t.success },
  cartTotalLabel: { fontWeight: 700, fontSize: '1rem', color: t.ink },
  checkoutLink: {
    display: 'block',
    textAlign: 'center',
    textDecoration: 'none',
    boxSizing: 'border-box',
    width: '100%',
    padding: '0.7rem',
    borderRadius: t.radius,
    background: t.brand,
    color: '#fff',
    fontWeight: 700,
  },
  cartSummary: { fontWeight: 600 },
  cartButton: {
    background: t.brand,
    color: '#fff',
    textDecoration: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '0.95rem',
    fontWeight: 700,
    padding: '0.55rem 1rem',
    borderRadius: t.radius,
  },
  img: {
    width: '100%', height: 240, objectFit: 'cover', display: 'block',
  },
  placeholder: {
    width: '100%',
    height: 240,
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#f3f4f6',
  },
  shimmer: {
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 37%, #f3f4f6 63%)',
    backgroundSize: '400% 100%',
    animation: 'seniorShimmer 1.4s ease infinite',
  },
  processing: { position: 'relative', color: t.muted, fontSize: '0.85rem' },
  caption: { padding: '0.4rem 0.6rem', fontSize: '0.8rem', color: t.muted },
  spinner: {
    width: 36,
    height: 36,
    borderRadius: '50%',
    border: '3px solid #e5e7eb',
    borderTopColor: t.brand,
    animation: 'seniorSpin 0.9s linear infinite',
  },

  // "Popular photo packages" strip shown under the flow gallery (Figma layout).
  // Flow-gallery header (Figma: centered "{name}'s Gallery" + subtitle).
  galleryHead: { textAlign: 'center', margin: '0.5rem 0 1.5rem' },
  galleryTitle: {
    fontSize: '1.5rem', fontWeight: 700, color: t.ink, margin: '0 0 0.15rem',
  },
  gallerySub: { color: t.muted, margin: 0, fontSize: '0.95rem' },
  popular: { marginTop: '2.5rem' },
  popularTitle: {
    fontSize: '1.15rem', fontWeight: 700, color: t.ink, margin: '0 0 0.9rem',
  },
  popularCard: {
    textAlign: 'left',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    padding: '0.75rem',
    background: t.surface,
    border: `1px solid ${t.border}`,
    borderRadius: t.radius,
    boxShadow: t.shadowSm,
  },
  popularThumb: {
    display: 'block',
    width: '100%',
    aspectRatio: '4 / 5',
    borderRadius: t.radiusSm,
    backgroundColor: t.mutedSurface,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  },
  popularName: {
    fontWeight: 600, color: t.ink, fontSize: '0.9rem', lineHeight: 1.2,
  },
  popularPrice: { color: t.brand, fontWeight: 700, fontSize: '0.95rem' },
  popularAdd: {
    marginTop: 'auto',
    width: '100%',
    background: t.brand,
    color: '#fff',
    border: 'none',
    borderRadius: t.radiusSm,
    padding: '0.5rem',
    fontWeight: 700,
    fontSize: '0.85rem',
    cursor: 'pointer',
  },
  viewMore: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.5rem',
    background: t.surface,
    color: t.brand,
    border: `2px solid ${t.brand}`,
    borderRadius: t.radius,
    padding: '0.7rem 1.5rem',
    fontWeight: 700,
    fontSize: '0.95rem',
    cursor: 'pointer',
  },
};

export default styles;
